import { useEffect, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useHub } from '../context';
import { isArmor, isLocked, loadRolled, type Rolled, type StashItem } from '../character';
import type { GodMark } from '../godrolls';
import type { HubChar } from '../hub';
import { armourTotal, keyOf, kindOf, TAGS, TIER_NAMES, type Spot, type Tag } from '../items';
import { Pic } from '../ui';

/** The Item Manager's larger views: Compare (an item's copies side by side) and the Organizer (every item as a table). */

/** DIM's thumbs-up, in the colour of what the roll is for: green PvE, red PvP, gold both. */
export function GodThumb({ mark }: { mark: GodMark }) {
  const kind = mark.pve && mark.pvp ? 'both' : mark.pvp ? 'pvp' : mark.pve ? 'pve' : 'any';
  return (
    <svg className={`im-god ${kind}`} viewBox="0 0 16 16" width="11" height="11" aria-label={`God roll (${kind})`}>
      <path d="M2 7h2.5v7H2zM6 14V7.2L8.6 2c1.2 0 1.9.9 1.6 2.1L9.6 6.5H13c.9 0 1.5.8 1.3 1.7l-1.2 4.8c-.2.6-.7 1-1.3 1z" fill="currentColor" />
    </svg>
  );
}

export function Compare({ base, spots, chars, godOf, rollOf, tags, setTag, close }: {
  base: StashItem; spots: Spot[]; chars: HubChar[]; godOf: (i: StashItem) => GodMark | undefined; rollOf: (i: StashItem) => Rolled | undefined;
  tags: Record<string, Tag>; setTag: (id: string, tag?: Tag) => void; close: () => void;
}) {
  const h = useHub();
  const [wide, setWide] = useState(false);   // false: copies of this item; true: every item of its type
  const pool = spots.filter((s) => (wide ? s.item.typeName === base.typeName && s.item.classType === base.classType : s.item.hash === base.hash || s.item.name === base.name))
    .sort((a, b) => Number(keyOf(b.item) === keyOf(base)) - Number(keyOf(a.item) === keyOf(base)) || (b.item.power ?? 0) - (a.item.power ?? 0)).slice(0, 24);
  const [rolls, setRolls] = useState<Record<string, Rolled | undefined>>({});
  const ids = pool.map((s) => s.item.instanceId).join(',');
  useEffect(() => {
    let on = true;
    (async () => {
      const t = await h.getToken();
      if (!t) return;
      // four at a time (Bungie answers each in about a second)
      const todo = pool.filter((s) => !rolls[s.item.instanceId] && !rollOf(s.item));   // those not already read with every roll
      const one = async (s: Spot) => {
        try { const r = await loadRolled(h.player!, t, s.item); if (on) setRolls((cur) => ({ ...cur, [s.item.instanceId]: r })); } catch { /* leave the column without stats */ }
      };
      await Promise.all([0, 1, 2, 3].map(async () => { for (let s = todo.shift(); s && on; s = todo.shift()) await one(s); }));
    })();
    return () => { on = false; };
  }, [ids]);   // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { const k = (e: KeyboardEvent) => { if (e.key === 'Escape') close(); }; window.addEventListener('keydown', k); return () => window.removeEventListener('keydown', k); }, [close]);
  const rolledOf = (s: Spot) => rollOf(s.item) ?? rolls[s.item.instanceId];
  const statNames = [...new Set(pool.flatMap((s) => rolledOf(s)?.stats.map((x) => x.name) ?? []))];
  const statOf = (s: Spot, n: string) => rolledOf(s)?.stats.find((x) => x.name === n)?.value;
  const where = (s: Spot) => (s.owner === null ? 'Vault' : `${chars.find((c) => c.id === s.owner)?.cls ?? ''}${s.worn ? ' (worn)' : ''}`);
  return createPortal(
    <div className="im-compare-back" onClick={close}>
      <div className="im-compare" role="dialog" aria-label={`Compare ${base.name}`} onClick={(e) => e.stopPropagation()}>
        <div className="im-compare-head">
          <b>Compare</b>
          <span className="im-sizes">
            <button type="button" className={wide ? '' : 'on'} style={{ width: 'auto', padding: '0 10px' }} onClick={() => setWide(false)}>Copies of {base.name}</button>
            <button type="button" className={wide ? 'on' : ''} style={{ width: 'auto', padding: '0 10px' }} onClick={() => setWide(true)}>Every {base.typeName}</button>
          </span>
          <span className="im-compare-count">{pool.length} item{pool.length === 1 ? '' : 's'}</span>
          <button type="button" className="im-compare-close" onClick={close} aria-label="Close">✕</button>
        </div>
        <div className="im-compare-scroll">
          <table>
            <thead>
              <tr>
                <th />
                {pool.map((s) => {
                  const g = godOf(s.item);
                  const id = keyOf(s.item);
                  return (
                    <th key={id} className={id === keyOf(base) ? 'base' : ''}>
                      <Pic src={s.item.icon} width={40} height={40} />
                      <div className="im-cmp-name">{s.item.name}</div>
                      <div className="im-cmp-sub">{s.item.power ?? ''} | {where(s)}</div>
                      <div className="im-cmp-marks">{g && <GodThumb mark={g} />}{isLocked(s.item) && <span>Locked</span>}{tags[id] && <span className={`t-${tags[id]}`}>{TAGS.find(([t]) => t === tags[id])?.[1]}</span>}</div>
                      <div className="im-cmp-tags">
                        {(['keep', 'junk'] as Tag[]).map((t) => (
                          <button key={t} type="button" className={`t-${t} ${tags[id] === t ? 'on' : ''}`} onClick={() => setTag(id, tags[id] === t ? undefined : t)}>{t === 'keep' ? 'Keep' : 'Junk'}</button>
                        ))}
                      </div>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {isArmor(base) && statNames.length > 0 && (
                <tr className="total">
                  <td className="im-cmp-stat">Total</td>
                  {(() => { const t = pool.map((s) => { const r = rolledOf(s); return r ? armourTotal(r) : undefined; }); const best = Math.max(...t.filter((v): v is number => v !== undefined)); return pool.map((s, k) => <td key={keyOf(s.item)} className={t[k] === best && pool.length > 1 ? 'best' : ''}>{t[k] ?? '…'}</td>); })()}
                </tr>
              )}
              {statNames.map((n) => {
                const vals = pool.map((s) => statOf(s, n));
                const best = Math.max(...vals.filter((v): v is number => v !== undefined));
                // the same everywhere, or a stat where more is not better: nothing to light
                const fixed = vals.every((v) => v === vals[0]) || /^(Rounds Per Minute|Draw Time|Charge Time)$/.test(n);
                return (
                  <tr key={n}>
                    <td className="im-cmp-stat">{n}</td>
                    {pool.map((s, k) => <td key={keyOf(s.item)} className={!fixed && vals[k] === best ? 'best' : ''}>{vals[k] ?? '…'}</td>)}
                  </tr>
                );
              })}
              <tr>
                <td className="im-cmp-stat">Perks</td>
                {pool.map((s) => (
                  <td key={keyOf(s.item)} className="im-cmp-perks">
                    {(rolledOf(s)?.perks ?? []).map((p) => <span key={p.hash} title={`${p.name}\n${p.type}`}><Pic src={p.icon} width={24} height={24} /></span>)}
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
          {!statNames.length && <p className="g-dim small" style={{ padding: 12 }}>Reading each item's roll from Bungie...</p>}
        </div>
      </div>
    </div>,
    document.body,
  );
}

// ---------------- the organizer: every item as a sortable table (DIM's Organizer) ----------------

type Col = { key: string; label: string; value: (s: Spot) => string | number; show?: (s: Spot) => ReactNode };

export function Organizer({ spots, matches, chars, godOf, tags, perkNames, elementIcons, open }: {
  spots: Spot[]; matches: (s: Spot) => boolean; chars: HubChar[]; godOf: (i: StashItem) => GodMark | undefined; tags: Record<string, Tag>;
  perkNames: (i: StashItem) => string[]; elementIcons?: Record<number, string>; open: (id: string, x: number, y: number) => void;
}) {
  const [kind, setKind] = useState<'weapon' | 'armour' | 'other'>('weapon');
  const [by, setBy] = useState<{ key: string; down: boolean }>({ key: 'power', down: true });
  const where = (s: Spot) => (s.owner === null ? 'Vault' : `${chars.find((c) => c.id === s.owner)?.cls ?? ''}${s.worn ? ' (worn)' : ''}`);
  const godWord = (s: Spot) => { const g = godOf(s.item); return !g ? '' : g.pve && g.pvp ? 'PvE + PvP' : g.pvp ? 'PvP' : g.pve ? 'PvE' : 'Pick'; };
  const cols: Col[] = [
    { key: 'name', label: 'Name', value: (s) => s.item.name, show: (s) => <span className="im-org-name"><Pic src={s.item.icon} width={28} height={28} />{s.item.name}</span> },
    { key: 'type', label: 'Type', value: (s) => s.item.typeName },
    { key: 'tier', label: 'Rarity', value: (s) => s.item.tier, show: (s) => TIER_NAMES[s.item.tier] ?? '' },
    ...(kind === 'weapon' ? [{ key: 'element', label: 'Element', value: (s: Spot) => s.item.damage, show: (s: Spot) => elementIcons?.[s.item.damage] ? <Pic src={elementIcons[s.item.damage]} width={16} height={16} /> : '' }] : []),
    { key: 'power', label: 'Power', value: (s) => s.item.power ?? 0 },
    { key: 'where', label: 'Where', value: where },
    { key: 'locked', label: 'Lock', value: (s) => Number(isLocked(s.item)), show: (s) => (isLocked(s.item) ? '🔒' : '') },
    { key: 'tag', label: 'Tag', value: (s) => tags[keyOf(s.item)] ?? '', show: (s) => { const t = TAGS.find(([k]) => k === tags[keyOf(s.item)]); return t ? <span className={`im-org-tag t-${t[0]}`}>{t[2]} {t[1]}</span> : ''; } },
    ...(kind === 'weapon' ? [{ key: 'god', label: 'God roll', value: godWord, show: (s: Spot) => { const g = godOf(s.item); return g ? <span className="im-org-god"><GodThumb mark={g} />{godWord(s)}</span> : ''; } }] : []),
    { key: 'perks', label: kind === 'armour' ? 'Perks and mods' : 'Perks', value: (s) => perkNames(s.item).join(', '), show: (s) => <span className="im-org-perks">{perkNames(s.item).join(', ')}</span> },
  ];
  const col = cols.find((c) => c.key === by.key) ?? cols[0];
  const rows = spots.filter((s) => matches(s) && kindOf(s.item) === kind).sort((a, b) => {
    const x = col.value(a), y = col.value(b);
    const c = typeof x === 'number' && typeof y === 'number' ? x - y : String(x).localeCompare(String(y));
    return by.down ? -c : c;
  });
  const shown = rows.slice(0, 400);
  return (
    <div className="im-org">
      <div className="im-org-bar">
        <span className="im-sizes">
          {(['weapon', 'armour', 'other'] as const).map((k) => <button key={k} type="button" className={kind === k ? 'on' : ''} style={{ width: 'auto', padding: '0 10px' }} onClick={() => setKind(k)}>{k === 'weapon' ? 'Weapons' : k === 'armour' ? 'Armour' : 'Ghosts, ships, sparrows'}</button>)}
        </span>
        <span className="g-dim small">{rows.length} items{rows.length > shown.length ? ` (first ${shown.length}; search to narrow)` : ''}. Click a heading to sort, an item for its card.</span>
      </div>
      <div className="im-org-scroll">
        <table>
          <thead>
            <tr>{cols.map((c) => (
              <th key={c.key} onClick={() => setBy({ key: c.key, down: by.key === c.key ? !by.down : ['power', 'tier', 'god', 'tag', 'locked'].includes(c.key) })} className={by.key === c.key ? 'on' : ''}>
                {c.label}{by.key === c.key ? (by.down ? ' ▾' : ' ▴') : ''}
              </th>
            ))}</tr>
          </thead>
          <tbody>
            {shown.map((s) => (
              <tr key={keyOf(s.item)} className={`t${s.item.tier}`} onClick={(e) => { e.stopPropagation(); open(keyOf(s.item), e.clientX, e.clientY); }}>
                {cols.map((c) => <td key={c.key} className={`c-${c.key}`}>{c.show ? c.show(s) : c.value(s)}</td>)}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
